import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { ArticleView, type TakeActions } from '../components/ArticleView'
import { PublishSheet } from '../components/PublishSheet'
import { ShareWritingSheet } from '../components/ShareWritingSheet'
import { shareExcerpt, type ShareCardContent } from '../lib/shareCard'
import { publicOrigin } from '../lib/env'
import { ReaderHeader } from '../components/reader/ReaderHeader'
import { WritingFooter } from '../components/reader/WritingFooter'
import { AuthorBlock } from '../components/reader/AuthorBlock'
import { TakenDock } from '../components/reader/TakenPanel'
import { SIDE_PANEL_DESK } from '../components/SidePanel'
import { useReadingLight } from '../components/reader/hooks'
import { useToast } from '../components/Toast'
import { btn, errorText, Loading, Notice } from '../components/ui'
import { dockClearance, ReadingControls } from '../components/reader/ReadingControls'
import { useSession } from '../lib/session'
import { useMediaQuery } from '../lib/hooks'
import { countWords, displayName, readMinutes, stripTags } from '../lib/text'
import type { Writing } from '../lib/types'
import { safeWriting } from '../lib/urls'

export function PreviewPage() {
  const { id = '' } = useParams()
  const { backend, profile } = useSession()
  const navigate = useNavigate()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const sheetOpen = params.get('publish') === '1'
  const [writing, setWriting] = useState<Writing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [readingLight, setReadingLight] = useReadingLight()
  const desk = useMediaQuery(SIDE_PANEL_DESK)
  const [publishedCount, setPublishedCount] = useState<number | undefined>(undefined)

  useEffect(() => {
    let alive = true
    backend
      .getWriting(id)
      .then((w) => {
        if (!alive) return
        if (w) setWriting(w)
        else setError('This writing doesn’t exist, or it isn’t yours.')
      })
      .catch((e: unknown) => alive && setError(errorText(e)))
    return () => {
      alive = false
    }
  }, [backend, id])

  // "N writings" in the author block, as readers will see it.
  useEffect(() => {
    let alive = true
    backend
      .listMyWritings()
      .then((ws) => alive && setPublishedCount(ws.filter((w) => w.status === 'published' && w.visibility === 'public').length))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [backend])

  useEffect(() => {
    if (writing) document.title = `Preview: ${writing.title || 'Untitled'}`
  }, [writing])

  // Readers' take bar and quote actions, shown as readers get them; in Preview nothing is kept.
  const previewTakes = useMemo<TakeActions>(() => {
    const notKept = () => toast('Readers take this with them. In Preview nothing is kept.')
    const copy = (text: string, done: string) =>
      navigator.clipboard?.writeText(text).then(
        () => toast(done),
        () => toast('Couldn’t copy — select the text instead.'),
      )
    return {
      lines: new Map(),
      quotes: new Set(),
      counts: {},
      busyId: null,
      takeLine: notKept,
      takePassage: notKept,
      takeQuote: notKept,
      copyLine: (excerpt) => void copy(`“${excerpt}”`, 'Line copied'),
      copyQuote: (block) => void copy(`“${block.type === 'thought' ? block.text : stripTags(block.html)}”`, 'Quote copied'),
      shareQuote: () => toast('Readers can share this quote. Sharing is off in Preview.'),
    }
  }, [toast])

  const openSheet = () => setParams({ publish: '1' })
  const closeSheet = useCallback(() => setParams({}, { replace: true }), [setParams])

  // Exactly what readers get: the same link/media filtering as the public page.
  const shown = useMemo(() => (writing ? safeWriting(writing) : null), [writing])

  // "Share as image" from the publish settings (Niki, 10/2): the share card, as the public page makes it.
  const [sharing, setSharing] = useState(false)
  const pageUrl = writing?.slug && profile?.handle ? `${publicOrigin()}/${profile.handle}/${writing.slug}` : null
  const cardContent = useMemo<ShareCardContent | null>(
    () =>
      shown && pageUrl
        ? {
            title: shown.title,
            subtitle: shown.subtitle,
            author: displayName(profile),
            excerpt: shareExcerpt(shown.blocks ?? []),
            url: pageUrl.replace(/^https?:\/\//, ''),
            minutes: readMinutes(shown.word_count || countWords(shown.title, shown.blocks)),
          }
        : null,
    [shown, pageUrl, profile],
  )

  if (error) return <Notice title="Couldn’t open this preview" body={error} action={<Link to="/">Back to Writings</Link>} />
  if (!writing || !shown) return <Loading label="Setting the page…" />

  const name = displayName(profile)
  return (
    <div className="min-h-dvh bg-canvas">
      {/* Burrow's dark brown (cream in dark mode), so it reads as the author's
          bar over the reader's page rather than part of it. */}
      <div className="sticky top-0 z-40 flex min-h-12 items-center justify-between gap-3 bg-foreground px-4 py-2 font-sans text-[13px] font-medium text-card sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <span className="rounded-md bg-accent px-2 py-[3px] text-[10.5px] font-bold tracking-[0.06em] text-accent-foreground">PREVIEW</span>
          <span className="hidden truncate text-card/75 sm:inline">This is how readers will see your writing</span>
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <Link
            to={`/w/${writing.id}`}
            className="whitespace-nowrap rounded-xl border border-card/30 px-3 py-1.5 text-card no-underline transition-colors hover:border-card/50 hover:bg-card/10"
          >
            Back to editor
          </Link>
          <button type="button" onClick={openSheet} className={btn.primary}>
            {writing.status === 'published' ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>

      {/* The reader's header, with Share inert. */}
      <ReaderHeader sticky={false} />
      <ReadingControls readingLight={{ on: readingLight, onChange: setReadingLight }} clearance={dockClearance({ desk, takenPill: desk })} />

      <main className="overflow-x-clip px-4 pt-10 sm:pt-[52px]">
        <ArticleView
          title={shown.title}
          subtitle={shown.subtitle}
          coverUrl={shown.cover_url}
          coverFrame={shown.cover_frame}
          blocks={shown.blocks}
          showTimes={shown.show_times}
          showSources={shown.show_sources}
          author={{ name, avatarUrl: profile?.avatar_url ?? null }}
          date={shown.published_at ?? new Date().toISOString()}
          wordCount={shown.word_count || countWords(shown.title, shown.blocks)}
          narration={shown.narration}
          readingLight={readingLight}
          takes={previewTakes}
        >
          {/* The author block readers get at the foot, Think Along in its place but inert
              (on the public page the author sees Share as image and Edit there instead). */}
          <AuthorBlock name={name} avatarUrl={profile?.avatar_url} count={publishedCount}>
            <button
              type="button"
              aria-disabled="true"
              title="Readers Think Along with you here"
              onClick={() => toast('Readers Think Along with you here')}
              className={`${btn.primaryLg} cursor-default hover:opacity-100`}
            >
              Think Along
            </button>
          </AuthorBlock>
        </ArticleView>
      </main>
      <WritingFooter />
      {/* Readers' Taken pill, inert: what they take collects there. (Phones get
          a Taken button only once something is taken, so there's none here.) */}
      {desk && <TakenDock preview open={false} count={0} />}

      {sheetOpen && (
        <PublishSheet
          writing={writing}
          profile={profile}
          onClose={closeSheet}
          onPublished={(w) => {
            const first = writing.status !== 'published'
            toast(first ? 'Published' : 'Updated')
            // Publishing lands on the share card ("Share your writing"): the first time, and after an update.
            navigate(`/${profile?.handle}/${w.slug}`, { state: { published: true, updated: !first } })
          }}
          onShareImage={
            cardContent
              ? () => {
                  closeSheet()
                  setSharing(true)
                }
              : undefined
          }
          onUnpublished={() => {
            setWriting({ ...writing, status: 'draft' })
            closeSheet()
            toast('Unpublished — it’s a draft again')
          }}
        />
      )}
      {sharing && cardContent && pageUrl && (
        // Closing it goes back to the settings it was opened from.
        <ShareWritingSheet
          content={cardContent}
          url={pageUrl}
          onClose={() => {
            setSharing(false)
            openSheet()
          }}
        />
      )}
    </div>
  )
}
